import React from 'react';
import { ChangeEvent, PureComponent, ReactElement } from 'react';
import { Col, Form, FormInstance, Input, Row, Select } from 'antd';
import { Globals } from 'constants/Globals';
import { Assignment, Component, Configuration, ConfigurationSpec, Processor, ProcessorHeader, ProcessorRef, ProcessorType } from '@methodset/endpoint-client-ts';
import { FormItem } from 'components/FormItem/FormItem';
import { ConfigurationEditor } from '../../ConfigurationEditor/ConfigurationEditor';
import { EditorHeader } from '../../EditorHeader/EditorHeader';
import { v4 as uuid } from "uuid";
import { CoreUtils } from 'utils/CoreUtils';
import { Spacer } from 'components/Spacer/Spacer';
import { Info } from 'components/Info/Info';
import { Panel } from 'components/Panel/Panel';
import { AssignmentsEditor } from './AssignmentsEditor/AssignmentsEditor';
import { ProcessorLoader } from '../ProcessorLoader/ProcessorLoader';
import update from 'immutability-helper';
import './ProcessorEditor.less';
import { ProcessorPicker } from './ProcessorPicker/ProcessorPicker';

export type CancelCallback = () => void;
export type DoneCallback = (component: Component, index: number) => void;

export type ProcessorEditorProps = typeof ProcessorEditor.defaultProps & {
    // Types of processors to include in selection list.
    types?: ProcessorType[],
    // The variables for the configuration.
    variableSpecs?: ConfigurationSpec[],
    // Processor headers.
    headers?: ProcessorHeader[],
    // A list of all components in the query.
    components: Component[],
    // The component being edited.
    component: Component | null,
    // The index of the component.
    index: number,
    // True to use for creating serial queries.
    serialFlow: boolean,
    // Allow variables to be assigned from processor outputs.
    allowAssignment?: boolean,
    // Called when editing is done.
    onDone: DoneCallback
    // Called with editing is canceled.
    onCancel: CancelCallback,
}

export type ProcessorEditorState = {
    // The processor for this component.
    processor?: Processor
    // The component being edited.
    component: Component
}

export class ProcessorEditor extends PureComponent<ProcessorEditorProps, ProcessorEditorState> {

    private formRef = React.createRef<FormInstance>();

    static defaultProps = {
        headers: [] as ProcessorHeader[],
        types: CoreUtils.enumToKeys(ProcessorType),
        serialFlow: false,
        allowAssignment: true
    }

    constructor(props: ProcessorEditorProps) {
        super(props);
        this.state = {
            // Make a deep copy of the component and store as state.
            // Since the configuration is an arbitrary nested object,
            // it has be be modified directly. The copy will be worked on
            // and if editing is canceled, it will be thrown away, as will
            // all of its modifications.
            component: props.component ? JSON.parse(JSON.stringify(props.component)) : ProcessorEditor.newComponent()
        };
        this.handleProcessorChange = this.handleProcessorChange.bind(this);
        this.handleProcessorLoad = this.handleProcessorLoad.bind(this);
        this.handleNameChange = this.handleNameChange.bind(this);
        this.handleDescriptionChange = this.handleDescriptionChange.bind(this);
        this.handleConfigurationChange = this.handleConfigurationChange.bind(this);
        this.handleAssignmentsChange = this.handleAssignmentsChange.bind(this);
        this.handleEditDone = this.handleEditDone.bind(this);
        this.handleEditError = this.handleEditError.bind(this);
        this.handleEditCancel = this.handleEditCancel.bind(this);
    }

    public static newComponent(id?: string): Component {
        return {
            id: id ? id : uuid(),
            name: undefined as any,
            type: undefined as any,
            processor: {} as ProcessorRef,
            configuration: {} as Configuration
        } as Component;
    }

    private handleProcessorChange(processorId: string, version: number) {
        const component = ProcessorEditor.newComponent();
        component.id = this.state.component.id;
        component.processor = {
            id: processorId,
            version: version
        } as ProcessorRef;
        this.setState({
            component: component,
            processor: undefined
        });
    }

    private handleProcessorLoad(processor: Processor): void {
        const name = this.state.component.name ? this.state.component.name : processor.name;
        const component = update(this.state.component, {
            type: { $set: processor.type },
            name: { $set: name }
        });
        this.setState({
            processor: processor,
            component: component
        });
    }

    private handleNameChange(e: ChangeEvent<HTMLInputElement>): void {
        const name = e.target.value;
        const component = update(this.state.component, {
            name: { $set: name }
        });
        this.setState({ component: component });
    }

    private handleDescriptionChange(e: ChangeEvent<HTMLTextAreaElement>): void {
        const description = e.target.value;
        const component = update(this.state.component, {
            description: { $set: description }
        });
        this.setState({ component: component });
    }

    private handleConfigurationChange(configuration: Configuration): void {
        const component = update(this.state.component, {
            configuration: { $set: configuration }
        });
        this.setState({ component: component });
    }

    private handleAssignmentsChange(assignments: Assignment[]): void {
        const component = update(this.state.component, {
            assignments: { $set: assignments }
        });
        this.setState({ component: component });
    }

    private handleEditDone(): void {
        this.props.onDone(this.state.component, this.props.index);
    }

    private handleEditError(e: any): void {
    }

    private handleEditCancel(): void {
        this.props.onCancel();
    }

    public render(): ReactElement {
        return (
            <Form
                ref={this.formRef}
                className="x-processoreditor"
                onFinish={this.handleEditDone}
                onFinishFailed={this.handleEditError}
            >
                <EditorHeader
                    title="Processor Editor"
                    onCancel={this.handleEditCancel}
                />
                <Row gutter={[32, 0]}>
                    <Col span={12}>
                        <ProcessorPicker
                            formRef={this.formRef}
                            types={this.props.types}
                            headers={this.props.headers}
                            processorId={this.state.component.processor.id}
                            version={this.state.component.processor.version}
                            onChange={this.handleProcessorChange}
                            onLoad={this.handleProcessorLoad}
                        />
                        <FormItem
                            {...Globals.FORM_LAYOUT}
                            formRef={this.formRef}
                            label="Name"
                            name="name"
                            info="The processor name."
                            rules={[{
                                required: true,
                                message: `Please select a processor name.`
                            }]}
                        >
                            <Input
                                placeholder="Enter a name."
                                value={this.state.component.name}
                                onChange={this.handleNameChange}
                            />
                        </FormItem>
                        <FormItem
                            {...Globals.FORM_LAYOUT}
                            formRef={this.formRef}
                            label="Description"
                            name="description"
                            info="The processor description."
                        >
                            <Input.TextArea
                                placeholder="Enter a description."
                                rows={3}
                                value={this.state.component.description}
                                onChange={this.handleDescriptionChange}
                            />
                        </FormItem>
                        {this.state.processor &&
                            <ConfigurationEditor
                                formRef={this.formRef}
                                configuration={this.state.component.configuration}
                                configurationSpecs={this.state.processor.configurationSpecs}
                                variableSpecs={this.props.variableSpecs}
                                optionalLabel="Optional Configurations"
                                onChange={this.handleConfigurationChange}
                            />
                        }
                    </Col>
                    <Col span={12}>
                        {this.state.processor &&
                            <Spacer direction="vertical" size="lg">
                                <Panel className="x-processoreditor-panel" inverse={true} title="Inputs">
                                    <Row>
                                        {this.state.processor.inputSpecs.length > 0 && this.state.processor.inputSpecs.map(spec => (
                                            <div key={spec.key} className="x-processoreditor-row">
                                                <Col span={12}>
                                                    <Info label={spec.name} info={spec.description} />
                                                </Col>
                                                <Col span={12}>
                                                    {CoreUtils.listToString(spec.types)}
                                                </Col>
                                            </div>
                                        ))}
                                        {this.state.processor.inputSpecs.length === 0 &&
                                            <Col span={12}>
                                                None
                                            </Col>
                                        }
                                    </Row>
                                </Panel>
                                <Panel className="x-processoreditor-panel" inverse={true} title="Outputs">
                                    <Row>
                                        {this.state.processor.outputSpecs.length > 0 && this.state.processor.outputSpecs.map(spec => (
                                            <div key={spec.key} className="x-processoreditor-row">
                                                <Col span={12}>
                                                    <Info label={spec.name} info={spec.description} />
                                                </Col>
                                                <Col span={12}>
                                                    {spec.type}
                                                </Col>
                                            </div>
                                        ))}
                                        {this.state.processor.outputSpecs.length === 0 &&
                                            <Col span={12}>
                                                None
                                            </Col>
                                        }
                                    </Row>
                                </Panel>
                                {this.props.allowAssignment && this.props.variableSpecs && this.props.variableSpecs.length > 0 &&
                                    <Panel className="x-processoreditor-panel" inverse={true} title="Assigned Variables">
                                        <AssignmentsEditor
                                            formRef={this.formRef}
                                            outputSpecs={this.state.processor.outputSpecs}
                                            variableSpecs={this.props.variableSpecs}
                                            assignments={this.state.component.assignments}
                                            onChange={this.handleAssignmentsChange}
                                        />
                                    </Panel>
                                }
                            </Spacer>
                        }
                    </Col>
                </Row>
            </Form >
        );
    }

}
